// add js methods that doesn't exist in older browsers
import "core-js";
import "intersection-observer";
import { action, flow } from "mobx";
import { Observer } from "mobx-react";
import React from "react";
import ReactDOM from "react-dom";
import "regenerator-runtime/runtime";
import "whatwg-fetch";
import { setupWtApp } from "../androidAndIosApp";
import { GetSettingsCompleteAsDictionaryResponse } from "../api/settings/getSettingsCompleteAsDictionary";
import { AdminSettingsTabType } from "../business-components/adminSettings/AdminSettingsTabType";
import { AppType } from "../constants/AppType";
import {
  PostMessageCVPEmbedConfig,
  PostMessageCVPEmbedReadyForSettings,
  PostMessageTypes,
} from "../constants/PostMessageTypes";
import { isAppTypeIframe } from "../getAppType";
import { textPreviewMode } from "../localization/textPreviewMode";
import { CvpSettings } from "../settings/CvpSettings";
import { getDefaultSettings } from "../settings/getDefaultSettings";
import { sanitizeSettings } from "../settings/sanitizeSettings";
import { setSettingsComplete, settings } from "../settings/settings";
import {
  BrowserHistoryContext,
  routerStore,
  setupRouterStore,
} from "../url/WSRouter";
import { getHostOrigin } from "../url/getHostOrigin";
import { lp } from "../utils/logPrefix";
import { guid } from "../utils/util";
import { App } from "./App";
import { iframeAvailableContentHeight } from "./IframeAvailableContentHeight";
import { isAdminPreview } from "../api/settings/isAdminPreview";

export function startApp() {
  setupWtApp();

  getHostOrigin();

  const browserHistory = setupRouterStore();

  let appStarted = false;
  function renderApp() {
    if (appStarted) return;
    appStarted = true;

    ReactDOM.render(
      <Observer
        render={() => (
          <BrowserHistoryContext.Provider value={browserHistory}>
            <App settings={settings} />
          </BrowserHistoryContext.Provider>
        )}
      />,
      document.getElementById(settings.get().idEmbed) ??
        document.getElementById("cvpreactroot")
    );
  }

  const loadId = guid();

  const startupSearchParams = new URL(window.location.href).searchParams;

  function startAppEmbedded() {
    if (!window.cvpSandboxType) {
      let st: any = startupSearchParams.get("cvpSandboxType");

      window.cvpSandboxType = st || null;
    }

    const handleCVPEmbedConfig = flow(function* (evt: MessageEvent<any>) {
      const msg: PostMessageCVPEmbedConfig = evt.data;

      if (
        msg.settingsComplete.idEmbed &&
        msg.settingsComplete.idEmbed !== "cvWebtempNo"
      ) {
        window.cvpEmbedType = "iframe";
      }

      let _settings = {
        ...msg.settingsComplete,
        iframe_overrideLocale: msg.iframe_overrideLocale,
        iframe_overrideTheme: msg.iframe_overrideTheme,
      };

      window.iframe_preventNavigationTakeover =
        msg.iframe_preventNavigationTakeover;

      if (isAdminPreview() && !msg.adminSettingsTabType) {
        // after navigating in the admin preview we only get settings from its embedscript
        // but that does not know the previewsettings. so we request them from the admin page
        _settings = yield requestAdminPreviewSettings();
      }

      setSettingsComplete(sanitizeSettings(_settings));

      iframeAvailableContentHeight.set(
        Math.floor(msg.iframeAvailableContentHeight)
      );
      textPreviewMode.set(
        msg.adminSettingsTabType === AdminSettingsTabType.textAndTranslations
      );

      if (msg.hostUrl) {
        routerStore.hostUrl = msg.hostUrl;
      }

      renderApp();
    });

    let reloadOnNextCVPEmbedConfig = false;

    window.addEventListener("message", async (evt) => {
      if (!evt.data) return;

      const handleTypes = [PostMessageTypes.CVPEmbedConfig];
      const messageType: PostMessageTypes = evt.data?.type;

      if (!handleTypes.includes(messageType)) {
        return;
      }

      if (!allowOrigin(evt.origin)) {
        console.warn(lp("Received message but origin not allowed"), {
          evt_origin: evt.origin,
          location_origin: window.location.origin,
          host_origin: getHostOrigin(),
          data: evt.data,
        });
        return;
      }

      console.log(lp("receiveMessage"), evt.data);

      if (messageType === PostMessageTypes.CVPEmbedConfig) {
        if (reloadOnNextCVPEmbedConfig) {
          window.location.reload();
          return;
        }

        handleCVPEmbedConfig(evt);
      }
    });

    // tell host that iframe is ready to receive settings
    const msg: PostMessageCVPEmbedReadyForSettings = {
      type: PostMessageTypes.CVPEmbedReadyForSettings,
      loadId,
      cvpIsLoggedIn: window.cvpIsLoggedIn,
    };

    try {
      window.parent.postMessage(msg, "*");
      console.log(lp("window.parent.postMessage"), msg);
    } catch (e) {
      console.error(lp(), e);
    }
  }

  function startAppStandalone() {
    // listen for setting changes from the admin page
    window.addEventListener("message", async (evt) => {
      if (!evt.data) return;

      const messageType: PostMessageTypes = evt.data?.type;
      if (messageType !== PostMessageTypes.CVPEmbedConfig) {
        return;
      }

      if (!allowOrigin(evt.origin, false)) {
        console.warn(lp("Received message but origin not allowed"), {
          evt_origin: evt.origin,
          location_origin: window.location.origin,
          host_origin: getHostOrigin(),
          data: evt.data,
        });
        return;
      }

      const msg: PostMessageCVPEmbedConfig = evt.data;
      setSettingsComplete(sanitizeSettings(msg.settingsComplete));
      textPreviewMode.set(
        msg.adminSettingsTabType === AdminSettingsTabType.textAndTranslations
      );
    });

    if (!window.cvpIdConcern) {
      setSettingsComplete(getDefaultSettings(isAppTypeIframe()));
      renderApp();
      return;
    }

    //  settings are embedded in the page for faster load
    const settingsKey =
      `cvpGetSettingsCompleteAsDictionary?` +
      `k=${window.cvpIdConcern}&idEmbed=${AppType.cvWebtempNo}`;

    const settingsComplete: GetSettingsCompleteAsDictionaryResponse =
      window.getReactQueryData()[settingsKey];

    if (!settingsComplete) {
      throw new Error("_settingsComplete missing");
    }

    const finalCompleteSettings = sanitizeSettings(
      settingsComplete.item as CvpSettings
    );

    setSettingsComplete(finalCompleteSettings);

    renderApp();
    return;
  }

  if (
    window.cvpEmbedType ||
    startupSearchParams.get("cvpEmbedType") ||
    inIframe()
  ) {
    startAppEmbedded();
  } else {
    startAppStandalone();
  }
}

function inIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

const allowOrigin = (senderOrigin: string, allowHostOrigin = true) => {
  if (senderOrigin === window.location.origin) return true;
  if (senderOrigin === "https://cv.webtemp.no") return true;
  if (senderOrigin === "https://utvikling-cv.webtemp.no") return true;
  if (senderOrigin === "https://bff.webtemp.no") return true;
  if (senderOrigin === "https://utvikling-bff.webtemp.no") return true;
  if (senderOrigin === "https://utvikling-secure.webtemp.no") return true;
  if (senderOrigin === "https://secure.webtemp.no") return true;
  if (senderOrigin === getHostOrigin() && allowHostOrigin) return true;

  return false;
};

const requestAdminPreviewSettings = () => {
  return new Promise((resolve) => {
    const id = guid();

    const f = (evt) => {
      const requiredType =
        PostMessageTypes.CVPAdminPreviewRequestSettingsResponse;

      if (evt.data?.type !== requiredType) return;
      if (evt.data?.guid !== id) return;

      console.log(lp("received admin preview settings"), evt.data);
      resolve(evt.data.settingsComplete);
      window.removeEventListener("message", f);
    };

    window.addEventListener("message", f);

    const msg = {
      type: PostMessageTypes.CVPAdminPreviewRequestSettings,
      guid: id,
    };

    window.parent.postMessage(msg, window.location.origin);
    console.log(lp("requesting admin preview settings"), msg);
  });
};
